import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import {
  makeStyles,
  useTheme,
  Paper,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Dialog,
  CircularProgress,
  Tabs,
  Tab,
  Checkbox,
  Box,
  Chip,
  Tooltip
} from "@material-ui/core";
import {
  Delete,
  Refresh,
  Send,
  Close,
  AttachFile,
  Restore,
  ReportProblemOutlined,
  DeleteSweep,
  HelpOutline,
  MoveToInbox as MoveToInboxIcon,
  GetApp,
  Visibility,
  Description,
  MailOutline as MailOutlineIcon
} from "@material-ui/icons";
import StandardTabs from "../../components/StandardTabs";
import Pagination from "@material-ui/lab/Pagination";
import PageHelpGuide from "../../components/PageHelpGuide";
import { EMAILS_HELP_DATA } from "../../constants/pageHelp/emailsHelp";
import { getStandardButtonStyles } from "../../styles/standardButtons";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  ...getStandardButtonStyles(theme),
  chatPapper: {
    display: "flex",
    height: "100%",
    flex: 1,
    minHeight: 0,
  },
  contactsWrapper: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflowY: "hidden",
    overflowX: "hidden",
    position: "relative",
    minWidth: "480px",
    boxSizing: "border-box",
    borderRadius: "16px",
    boxShadow: theme.palette.type === "light" 
      ? `0 0 10px ${theme.palette.primary.main}40`
      : `0 0 12px ${theme.palette.primary.main}80`,
    border: theme.palette.type === "dark" 
      ? `1px solid ${theme.palette.primary.main}` 
      : `1px solid ${theme.palette.primary.main}80`,
    backgroundColor: theme.palette.type === "light" ? "#FFFFFF" : "#2A2D32 !important",
  },
  messagesWrapper: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    flexGrow: 1,
    marginLeft: "16px",
    boxSizing: "border-box",
    minWidth: 0,
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: theme.palette.type === "light" 
      ? `0 0 10px ${theme.palette.primary.main}40`
      : `0 0 12px ${theme.palette.primary.main}80`,
    border: theme.palette.type === "dark" 
      ? `1px solid ${theme.palette.primary.main}` 
      : `1px solid ${theme.palette.primary.main}80`,
    backgroundColor: theme.palette.type === "light" ? "#FFFFFF" : "#2A2D32 !important",
  },
  welcomeMsg: {
    background: "transparent",
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "center",
    height: "100%",
    textAlign: "center",
    border: "none",
    boxShadow: "none",
    color: theme.palette.type === "light" ? "#64748B" : "#8C939D",
  },
  resizer: {
    width: "4px",
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    cursor: "col-resize",
    zIndex: 10,
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
    },
  },
  root: {
    backgroundColor: theme.palette.type === 'dark' ? '#2A2D32 !important' : theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(1),
    height: "100%",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  sidebar: {
    height: "100%",
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingLeft: 20,
    paddingRight: theme.spacing(2),
    display: "flex",
    flexDirection: "column"
  },
  content: {
    height: "100%",
    paddingLeft: theme.spacing(2),
    display: "flex",
    flexDirection: "column"
  },
  emailList: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStylesSoft,
  },
  paginationWrapper: {
    padding: "8px 12px",
    borderTop: theme.palette.type === "dark" 
      ? "1px solid rgba(255, 255, 255, 0.08)" 
      : "1px solid rgba(0, 0, 0, 0.08)",
    backgroundColor: theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.2)" : "#ffffff",
    borderRadius: 8,
    marginTop: 6,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  paginationText: {
    fontSize: "11.5px",
    color: theme.palette.type === "dark" ? "#94a3b8" : "rgb(104, 121, 146)",
    fontWeight: 600,
    marginBottom: "5px",
    letterSpacing: "0.3px",
  },
  pagination: {
    "& .MuiPaginationItem-root": {
      fontSize: "0.8rem",
      fontWeight: 600,
      minWidth: "28px",
      height: "28px",
      padding: 0,
      margin: "0 2px",
      borderRadius: "6px",
      color: theme.palette.type === "dark" ? "#f1f5f9" : "#334155",
      backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.04)",
      border: theme.palette.type === "dark" 
        ? "1px solid rgba(255, 255, 255, 0.12)" 
        : "1px solid rgba(0, 0, 0, 0.1)",
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.14)" : "rgba(0, 0, 0, 0.08)",
        color: theme.palette.type === "dark" ? "#ffffff" : "#0f172a",
      },
    },
    "& .MuiPaginationItem-page.Mui-selected": {
      backgroundColor: theme.palette.primary.main + " !important",
      color: "#ffffff !important",
      borderColor: "transparent !important",
      fontWeight: "bold",
      boxShadow: theme.palette.type === "dark" 
        ? ("0 0 10px " + theme.palette.primary.main + "59") 
        : "0 2px 5px rgba(0, 0, 0, 0.2)",
    },
    "& .MuiPaginationItem-icon": {
      color: theme.palette.type === "dark" ? "#f1f5f9" : "#334155",
    },
    "& .Mui-disabled": {
      opacity: "0.3 !important",
    }
  },
  emailDetails: {
    padding: theme.spacing(3),
    height: "100%",
    boxSizing: "border-box",
    backgroundColor: "transparent",
    color: theme.palette.type === "dark" ? "#E2E8F0" : "#000000",
    border: "none",
    boxShadow: "none",
    borderRadius: 0,
    overflowY: "auto",
    overflowX: "auto",
    ...theme.scrollbarStylesSoft,
  },
  neonTab: {
    color: theme.palette.type === 'dark' ? '#94A3B8' : '#64748B',
    transition: 'all 0.3s ease',
    minWidth: 80,
    '&.Mui-selected': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    }
  },
  neonTabsIndicator: {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: '3px 3px 0 0'
  },
  accountBox: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    border: theme.palette.type === 'dark' ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: 12,
    backgroundColor: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  dialogPaper: {
    backgroundColor: theme.palette.type === 'dark' ? '#1E2126' : '#FFFFFF',
    borderRadius: 16,
    border: `1px solid ${theme.palette.primary.main}40`,
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.55)',
    color: theme.palette.type === 'dark' ? '#E2E8F0' : theme.palette.text.primary,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
  },
  dialogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
    '& h2': {
      display: 'flex',
      alignItems: 'center',
      fontSize: '1.15rem',
      fontWeight: 600,
      margin: 0,
      color: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
    }
  },
  dialogContent: {
    padding: theme.spacing(3),
    overflowY: 'auto',
    flex: '1 1 auto',
    ...theme.scrollbarStylesSoft,
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)',
  },
  formInputContainer: {
    marginBottom: theme.spacing(2)
  },
  formInputLabel: {
    color: theme.palette.type === 'dark' ? '#CBD5E1' : '#475569',
    fontSize: '0.85rem',
    marginBottom: 6,
    display: 'block',
    fontWeight: 500
  },
  formInput: {
    width: "100%",
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 0, 0, 0.25)' : '#F8FAFC',
    border: theme.palette.type === 'dark' ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.15)',
    borderRadius: 8,
    padding: "10px 14px",
    color: theme.palette.type === 'dark' ? '#FFFFFF' : '#1E293B',
    outline: "none",
    fontSize: "0.95rem",
    transition: "all 0.2s ease",
    boxSizing: "border-box",
    "&:focus": {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`,
    },
    "&::placeholder": {
      color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)'
    }
  },
  advancedConfigBox: {
    border: theme.palette.type === 'dark' ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    borderRadius: 12,
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)"
  },
  attachmentChip: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
    color: theme.palette.type === 'dark' ? '#E2E8F0' : '#1E293B',
    border: `1px solid ${theme.palette.divider}`,
  },
  attachmentCard: {
    display: "flex",
    flexDirection: "column",
    borderRadius: 10,
    border: theme.palette.type === 'dark' ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
    backgroundColor: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.02)",
    padding: 8,
    width: 170,
    position: "relative",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.07)" : "rgba(0, 0, 0, 0.04)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    }
  },
  attachmentCardDoc: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    border: theme.palette.type === 'dark' ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
    backgroundColor: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.02)",
    padding: "8px 12px",
    gap: 10,
    maxWidth: 280,
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    }
  },
  composeAttachmentItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 8,
    border: theme.palette.type === 'dark' ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.12)",
    backgroundColor: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.04)",
    maxWidth: 260,
  }
}));

const formatBytes = (bytes) => {
  if (bytes === undefined || bytes === null || isNaN(bytes) || bytes === 0) return "";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i] || "B"}`;
};

const isImageAttachment = (att) => {
  if (!att) return false;
  if (att.contentType && att.contentType.startsWith("image/")) return true;
  if (att.type && att.type.startsWith("image/")) return true;
  const name = (att.filename || att.name || "").toLowerCase();
  return /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name);
};

const getAttachmentUrl = (att) => {
  if (!att) return "";
  if (att.data) {
    return att.data.startsWith("data:") ? att.data : `data:${att.contentType || "application/octet-stream"};base64,${att.data}`;
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (att.url) {
    return att.url.startsWith("http") ? att.url : `${backendUrl}${att.url}`;
  }
  if (att.path) {
    const publicIdx = att.path.indexOf("/public/");
    if (publicIdx !== -1) {
      return `${backendUrl}${att.path.substring(publicIdx)}`;
    }
  }
  return "";
};

const ComposeAttachmentItem = ({ file, onRemove, onPreview, classes }) => {
  const isImage = isImageAttachment(file);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (isImage) {
      const url = URL.createObjectURL(file);
      setBlobUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [file, isImage]);

  return (
    <div className={classes.composeAttachmentItem}>
      {isImage && blobUrl ? (
        <Tooltip title={i18n.t("common.emailInbox.clickToExpand", "Clic para ampliar")}>
          <img
            src={blobUrl}
            alt={file.name}
            onClick={() => onPreview({ url: blobUrl, filename: file.name, file: true })}
            style={{ width: 42, height: 42, objectFit: "cover", borderRadius: 6, cursor: "pointer", border: "1px solid rgba(255,255,255,0.15)" }}
          />
        </Tooltip>
      ) : (
        <Description style={{ fontSize: 30, opacity: 0.75 }} />
      )}
      <div style={{ overflow: "hidden", minWidth: 0, flex: 1 }}>
        <Typography variant="caption" style={{ fontWeight: 600, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={file.name}>
          {file.name}
        </Typography>
        <Typography variant="caption" style={{ color: "#94a3b8", fontSize: "0.72rem", display: "block" }}>
          {formatBytes(file.size)}
        </Typography>
      </div>
      <IconButton size="small" onClick={onRemove} style={{ padding: 4 }}>
        <Close style={{ fontSize: 16 }} />
      </IconButton>
    </div>
  );
};

const Emails = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const [maxConnections, setMaxConnections] = useState(
    (user?.company?.customMaxEmails !== null && user?.company?.customMaxEmails !== undefined)
      ? Number(user?.company?.customMaxEmails)
      : (user?.company?.plan?.maxConnEmail !== undefined && user?.company?.plan?.maxConnEmail !== null ? Number(user?.company?.plan?.maxConnEmail) : 1)
  );
  const [accounts, setAccounts] = useState([]);
  const [emails, setEmails] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailsManagerWidth, setEmailsManagerWidth] = useState(520);
  const minEmailsManagerWidth = 480;
  const maxEmailsManagerWidth = 700;
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(0);

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    const delta = e.clientX - dragStartXRef.current;
    const newWidth = Math.max(minEmailsManagerWidth, Math.min(maxEmailsManagerWidth, dragStartWidthRef.current + delta));
    setEmailsManagerWidth(newWidth);
  }, []);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = (e) => {
    isDraggingRef.current = true;
    dragStartXRef.current = e.clientX;
    dragStartWidthRef.current = emailsManagerWidth;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    e.preventDefault();
  };

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const [selectedEmailIds, setSelectedEmailIds] = useState([]);
  const [folder, setFolder] = useState("inbox"); // "inbox" | "sent" | "spam" | "trash"
  const [folderCounts, setFolderCounts] = useState({ inbox: 0, sent: 0, spam: 0, trash: 0 });
  
  const [openSettings, setOpenSettings] = useState(false);
  const [openCompose, setOpenCompose] = useState(false);
  const [previewModalMedia, setPreviewModalMedia] = useState(null);
  
  const [loading, setLoading] = useState(false);

  // Form states for account
  const [accForm, setAccForm] = useState({ name: "", email: "", password: "", imapHost: "", imapPort: 993, smtpHost: "", smtpPort: 465 });

  // Form states for compose
  const [composeForm, setComposeForm] = useState({ to: "", subject: "", bodyHtml: "" });
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const handleDownloadAttachment = (att) => {
    const url = getAttachmentUrl(att);
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = att.filename || att.name || "adjunto";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const PAGE_SIZE = 200;

  const fetchEmails = useCallback(async (accountId, currentFolder, page = 1) => {
    try {
      setLoading(true);
      const activeFolder = currentFolder || folder;
      const { data } = await api.get(`/emails`, {
        params: { emailAccountId: accountId, folder: activeFolder, pageNumber: page, pageSize: PAGE_SIZE }
      });
      setEmails(data.emails || []);
      setSelectedEmail(null);
      setSelectedEmailIds([]);
      setHasMore(data.hasMore);
      if (data.folderCounts) {
        setFolderCounts(data.folderCounts);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [folder]);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/emails/accounts");
      setAccounts(data);
      if (data.length > 0 && !selectedAccount) {
        setSelectedAccount(data[0]);
        setPageNumber(1);
        fetchEmails(data[0].id, folder, 1);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, folder, fetchEmails]);

  useEffect(() => {
    fetchAccounts();
    if (user?.companyId) {
      api.get(`/companies/listPlan/${user.companyId}`).then(({ data }) => {
        if (data) {
          const limit = (data.customMaxEmails !== null && data.customMaxEmails !== undefined)
            ? Number(data.customMaxEmails)
            : (data.plan?.maxConnEmail !== undefined && data.plan?.maxConnEmail !== null ? Number(data.plan.maxConnEmail) : 1);
          setMaxConnections(limit);
        }
      }).catch(err => console.error("Error loading email plan limit:", err));
    }
  }, [user?.companyId, fetchAccounts]);

  const handlePageChange = (event, value) => {
    setPageNumber(value);
    setSelectedEmail(null);
    setSelectedEmailIds([]);
    if (selectedAccount) {
      fetchEmails(selectedAccount.id, folder, value);
    }
  };

  const handleFolderChange = (event, newFolder) => {
    setFolder(newFolder);
    setPageNumber(1);
    setSelectedEmail(null);
    setSelectedEmailIds([]);
    if (selectedAccount) {
      fetchEmails(selectedAccount.id, newFolder, 1);
    }
  };

  const loadMore = () => {
    setPageNumber(prev => {
       const next = prev + 1;
       if (selectedAccount) {
          fetchEmails(selectedAccount.id, folder, next);
       }
       return next;
    });
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleSyncEmail = async (accountId) => {
    try {
      setLoading(true);
      await api.post(`/emails/accounts/${accountId}/sync`);
      toast.success("Sincronización iniciada en segundo plano...");
      setTimeout(() => {
        setPageNumber(1);
        fetchEmails(accountId, folder, 1);
      }, 3000);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEmailIds.length === 0) return;
    const isTrash = folder === "trash";
    const confirmMessage = isTrash
      ? `¿Seguro que deseas eliminar definitivamente ${selectedEmailIds.length} correos?`
      : `¿Seguro que deseas mover ${selectedEmailIds.length} correos a la papelera?`;

    if (window.confirm(confirmMessage)) {
      try {
        setLoading(true);
        await api.post("/emails/remove-bulk", { emailIds: selectedEmailIds });
        toast.success(isTrash ? "Correos eliminados definitivamente" : "Correos movidos a la papelera");
        setSelectedEmailIds([]);
        setSelectedEmail(null);
        fetchEmails(selectedAccount.id, folder, 1);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSelectEmail = (emailId) => {
    setSelectedEmailIds(prev => {
      if (prev.includes(emailId)) {
        return prev.filter(id => id !== emailId);
      } else {
        return [...prev, emailId];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedEmailIds(emails.map(email => email.id));
    } else {
      setSelectedEmailIds([]);
    }
  };

  const handleSaveAccount = async () => {
    if (accounts.length >= maxConnections) {
      toast.error(`Has alcanzado el límite máximo de ${maxConnections} cuentas de correo.`);
      return;
    }
    try {
      await api.post("/emails/accounts", accForm);
      toast.success("Cuenta añadida correctamente");
      setOpenSettings(false);
      setAccForm({ name: "", email: "", password: "", imapHost: "", imapPort: 993, smtpHost: "", smtpPort: 465 });
      fetchAccounts();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteAccount = async (id) => {
    if (window.confirm("¿Seguro que deseas eliminar esta cuenta?")) {
      try {
        await api.delete(`/emails/accounts/${id}`);
        toast.success("Cuenta eliminada");
        fetchAccounts();
      } catch (err) {
        toastError(err);
      }
    }
  };

  // Manejador de archivos adjuntos
  const handleFileChange = (e) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setAttachments(prev => [...prev, ...selectedFiles]);
    }
    e.target.value = null;
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleSendEmail = async () => {
    if (!selectedAccount) {
      toast.error("Selecciona una cuenta remitente");
      return;
    }
    if (!composeForm.to || !composeForm.subject) {
      toast.error("Por favor completa el destinatario y el asunto");
      return;
    }
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("emailAccountId", selectedAccount.id);
      formData.append("to", composeForm.to);
      formData.append("subject", composeForm.subject);
      formData.append("bodyHtml", composeForm.bodyHtml || "");
      
      attachments.forEach(file => {
        formData.append("medias", file);
      });

      await api.post("/emails/send", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Correo enviado con éxito");
      setOpenCompose(false);
      setComposeForm({ to: "", subject: "", bodyHtml: "" });
      setAttachments([]);
      fetchEmails(selectedAccount.id, folder, 1);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  // Acciones individuales sobre correos
  const handleDeleteEmail = async (emailId) => {
    const isTrash = folder === "trash";
    const confirmMsg = isTrash
      ? "¿Seguro que deseas eliminar definitivamente este correo?"
      : "¿Mover este correo a la papelera?";
    if (window.confirm(confirmMsg)) {
      try {
        setLoading(true);
        await api.delete(`/emails/${emailId}`);
        toast.success(isTrash ? "Correo eliminado permanentemente" : "Correo movido a la papelera");
        if (selectedEmail?.id === emailId) {
          setSelectedEmail(null);
        }
        fetchEmails(selectedAccount.id, folder, 1);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRestoreEmail = async (emailId) => {
    try {
      setLoading(true);
      await api.put(`/emails/${emailId}`, { folder: "inbox" });
      toast.success("Correo restaurado a la bandeja de entrada");
      if (selectedEmail?.id === emailId) {
        setSelectedEmail(null);
      }
      fetchEmails(selectedAccount.id, folder, 1);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMoveToSpam = async (emailId) => {
    try {
      setLoading(true);
      await api.put(`/emails/${emailId}`, { folder: "spam" });
      toast.success("Correo movido a Spam");
      if (selectedEmail?.id === emailId) {
        setSelectedEmail(null);
      }
      fetchEmails(selectedAccount.id, folder, 1);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmptyTrash = async () => {
    if (!selectedAccount) return;
    if (window.confirm("¿Seguro que deseas vaciar toda la papelera? Esta acción eliminará permanentemente todos los correos.")) {
      try {
        setLoading(true);
        await api.delete("/emails/trash/empty", {
          params: { emailAccountId: selectedAccount.id }
        });
        toast.success("Papelera vaciada correctamente");
        setSelectedEmail(null);
        setSelectedEmailIds([]);
        fetchEmails(selectedAccount.id, "trash", 1);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const getFolderTitle = () => {
    switch (folder) {
      case "sent": return i18n.t("common.emailInbox.sent");
      case "spam": return i18n.t("common.emailInbox.spam");
      case "trash": return i18n.t("common.emailInbox.trash");
      default: return i18n.t("common.emailInbox.inbox");
    }
  };

  return (
    <MainContainer>
      <div className={classes.chatPapper}>
        <div 
          className={classes.contactsWrapper} 
          style={{ width: `${emailsManagerWidth}px` }}
        >
          {/* Header */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{ marginTop: 36, marginBottom: 20, paddingLeft: 28, paddingRight: 20, gap: 14 }}
          >
            <Box display="flex" alignItems="center" style={{ gap: 14 }}>
              <PageHelpGuide
                helpData={EMAILS_HELP_DATA}
                tooltipText="Guía y Ayuda del Inbox de Correo (Haz clic para ver)"
              />
              <Typography
                variant="h5"
                style={{
                  fontWeight: 700,
                  color: theme.palette.type === "dark" ? "#FFF" : theme.palette.primary.main,
                  letterSpacing: "-0.5px",
                }}
              >
                {i18n.t("common.emailInbox.title", "Inbox de Correo")}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Tooltip title={`${i18n.t("common.emailInbox.configureAccounts")} (${accounts.length}/${maxConnections})`}>
                <Button 
                  variant="outlined" 
                  size="small" 
                  className={classes.secondaryButton} 
                  onClick={() => setOpenSettings(true)}
                  style={{ height: 32, fontSize: "0.75rem", padding: "4px 8px", whiteSpace: "nowrap" }}
                >
                  Cuentas ({accounts.length}/{maxConnections})
                </Button>
              </Tooltip>
              <Button 
                variant="outlined" 
                size="small" 
                className={classes.applyButton} 
                startIcon={<Send style={{ fontSize: 14 }} />} 
                onClick={() => setOpenCompose(true)} 
                disabled={accounts.length === 0}
                style={{ height: 32, fontSize: "0.75rem", padding: "4px 10px", whiteSpace: "nowrap" }}
              >
                {i18n.t("common.emailInbox.compose")}
              </Button>
            </Box>
          </Box>

          {/* Cuentas */}
          <Box style={{ paddingLeft: 28, paddingRight: 20, marginBottom: 12 }}>
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 6, color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B" }}>
              {i18n.t("common.emailInbox.myAccounts")}
            </Typography>
            <div style={{ display: "flex", overflowX: "auto", gap: 8, paddingBottom: 4 }}>
              {accounts.map(acc => (
                <Button 
                  key={acc.id} 
                  variant={selectedAccount?.id === acc.id ? "contained" : "outlined"} 
                  color="primary" 
                  size="small"
                  style={{
                    borderRadius: 8,
                    textTransform: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    flexShrink: 0
                  }}
                  onClick={() => {
                    setSelectedAccount(acc);
                    setPageNumber(1);
                    fetchEmails(acc.id, folder, 1);
                  }}
                >
                  {acc.name}
                </Button>
              ))}
            </div>
          </Box>

          {/* Folder Tabs */}
          <div style={{ marginBottom: 12, paddingLeft: 20, paddingRight: 20, width: "100%", boxSizing: "border-box" }}>
            <StandardTabs
              value={folder}
              onChange={handleFolderChange}
              fullWidth
              items={[
                {
                  value: "inbox",
                  label: i18n.t("common.emailInbox.inbox"),
                  badge: folderCounts.inbox,
                  icon: <MoveToInboxIcon style={{ fontSize: 17 }} />,
                },
                {
                  value: "sent",
                  label: i18n.t("common.emailInbox.sent"),
                  badge: folderCounts.sent,
                  icon: <Send style={{ fontSize: 17 }} />,
                },
                {
                  value: "spam",
                  label: i18n.t("common.emailInbox.spam"),
                  badge: folderCounts.spam,
                  icon: <ReportProblemOutlined style={{ fontSize: 17 }} />,
                },
                {
                  value: "trash",
                  label: i18n.t("common.emailInbox.trash"),
                  badge: folderCounts.trash,
                  icon: <DeleteSweep style={{ fontSize: 17 }} />,
                },
              ]}
            />
          </div>

          {/* Actions Toolbar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, padding: "0 20px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Checkbox 
                checked={emails.length > 0 && selectedEmailIds.length === emails.length}
                indeterminate={selectedEmailIds.length > 0 && selectedEmailIds.length < emails.length}
                onChange={handleSelectAll}
                color="primary"
                size="small"
              />
              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 500, userSelect: "none" }}>
                {i18n.t("common.emailInbox.selectAll", "Seleccionar todos")}
              </Typography>
            </div>

            <div style={{ display: "flex", alignItems: "center" }}>
              {folder === "trash" && emails.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.dangerButton}
                  style={{ marginRight: 6, height: 28, fontSize: "0.72rem", padding: "2px 8px" }}
                  startIcon={<DeleteSweep style={{ fontSize: 14 }} />}
                  onClick={handleEmptyTrash}
                  disabled={loading}
                >
                  {i18n.t("common.emailInbox.emptyTrash")}
                </Button>
              )}
              {selectedEmailIds.length > 0 && (
                <Button 
                  size="small" 
                  variant="outlined" 
                  className={classes.dangerButton}
                  style={{ marginRight: 6, height: 28, fontSize: "0.72rem", padding: "2px 8px" }}
                  onClick={handleBulkDelete}
                  disabled={loading}
                >
                  {folder === "trash" ? i18n.t("common.emailInbox.deletePermanently", "Eliminar") : i18n.t("common.emailInbox.moveToTrash")} ({selectedEmailIds.length})
                </Button>
              )}
              <Button 
                size="small" 
                variant="outlined" 
                className={classes.applyButton}
                style={{ marginRight: 4, height: 28, fontSize: "0.72rem", padding: "2px 8px" }}
                startIcon={<Refresh style={{ fontSize: 14 }} />}
                onClick={() => selectedAccount && handleSyncEmail(selectedAccount.id)}
                disabled={loading || !selectedAccount}
              >
                {i18n.t("common.emailInbox.sync", "Sincronizar")}
              </Button>
              <IconButton size="small" onClick={() => {
                if (selectedAccount) {
                  setPageNumber(1);
                  fetchEmails(selectedAccount.id, folder, 1);
                }
              }} disabled={loading}>
                <Refresh fontSize="small" />
              </IconButton>
            </div>
          </div>

          {/* Email List */}
          {loading && pageNumber === 1 ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4} flex={1}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <List className={classes.emailList} style={{ paddingLeft: 12, paddingRight: 12 }}>
                {emails.map(email => (
                  <ListItem 
                    button 
                    key={email.id} 
                    selected={selectedEmail?.id === email.id}
                    onClick={() => setSelectedEmail(email)}
                    style={{ borderRadius: 8, marginBottom: 4 }}
                  >
                    <Checkbox
                      checked={selectedEmailIds.includes(email.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectEmail(email.id);
                      }}
                      color="primary"
                      size="small"
                    />
                    <ListItemText 
                      primary={email.subject || "(Sin Asunto)"} 
                      secondary={
                        <>
                          <span style={{ display: 'block', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                            {email.from}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: theme.palette.primary.main, fontWeight: '500' }}>
                            {new Date(email.date).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </>
                      } 
                      primaryTypographyProps={{ style: { fontWeight: email.isRead ? 'normal' : 'bold', fontSize: '0.9rem' } }}
                    />
                  </ListItem>
                ))}
                {emails.length === 0 && !loading && (
                  <Box p={3} textAlign="center">
                    <Typography variant="body2" color="textSecondary">{i18n.t("common.emailInbox.noEmailsInFolder", "No hay correos en esta carpeta")}</Typography>
                  </Box>
                )}
              </List>

              {(() => {
                const totalEmails = folderCounts[folder] || 0;
                const totalPages = Math.ceil(totalEmails / PAGE_SIZE);
                if (totalEmails === 0) return null;
                return (
                  <div className={classes.paginationWrapper} style={{ margin: "6px 16px 12px 16px" }}>
                    <span className={classes.paginationText}>
                      {`${(pageNumber - 1) * PAGE_SIZE + 1} - ${Math.min(pageNumber * PAGE_SIZE, totalEmails)} de ${totalEmails} correos`}
                    </span>
                    {totalPages > 1 && (
                      <Pagination
                        count={totalPages}
                        page={pageNumber}
                        onChange={handlePageChange}
                        size="small"
                        color="primary"
                        shape="rounded"
                        siblingCount={0}
                        boundaryCount={1}
                        className={classes.pagination}
                      />
                    )}
                  </div>
                );
              })()}
            </>
          )}

          {/* Resizer */}
          <div 
            onMouseDown={handleMouseDown} 
            className={classes.resizer}
          />
        </div>

        {/* Right Column: Messages / Details Wrapper */}
        <div className={classes.messagesWrapper}>
          {selectedEmail ? (
            <div className={classes.emailDetails}>
              <div style={{ maxWidth: "850px", margin: "0 auto", width: "100%" }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ marginBottom: 16 }}>
                  <div>
                    <Typography variant="h5" style={{ fontWeight: 600 }}>{selectedEmail.subject || "(Sin Asunto)"}</Typography>
                    <Typography variant="subtitle2" color="textSecondary" style={{ marginTop: 4 }}>
                      De: {selectedEmail.from}
                    </Typography>
                    {selectedEmail.to && (
                      <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                        Para: {selectedEmail.to}
                      </Typography>
                    )}
                    <Typography variant="caption" color="primary" style={{ fontWeight: 600, display: 'block', marginTop: 2 }}>
                      {new Date(selectedEmail.date).toLocaleString()}
                    </Typography>
                  </div>

                  {/* Acciones de correo */}
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    {folder === "trash" ? (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          className={classes.applyButton}
                          startIcon={<Restore style={{ fontSize: 16 }} />}
                          onClick={() => handleRestoreEmail(selectedEmail.id)}
                          disabled={loading}
                        >
                          {i18n.t("common.emailInbox.restore")}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          className={classes.dangerButton}
                          startIcon={<Delete style={{ fontSize: 16 }} />}
                          onClick={() => handleDeleteEmail(selectedEmail.id)}
                          disabled={loading}
                        >
                          {i18n.t("common.emailInbox.deletePermanently", "Eliminar")}
                        </Button>
                      </>
                    ) : folder === "spam" ? (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          className={classes.applyButton}
                          startIcon={<Restore style={{ fontSize: 16 }} />}
                          onClick={() => handleRestoreEmail(selectedEmail.id)}
                          disabled={loading}
                        >
                          {i18n.t("common.emailInbox.notSpam")}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          className={classes.dangerButton}
                          startIcon={<Delete style={{ fontSize: 16 }} />}
                          onClick={() => handleDeleteEmail(selectedEmail.id)}
                          disabled={loading}
                        >
                          {i18n.t("common.emailInbox.moveToTrash")}
                        </Button>
                      </>
                    ) : (
                      <>
                        {folder === "inbox" && (
                          <Button
                            size="small"
                            variant="outlined"
                            className={classes.secondaryButton}
                            startIcon={<ReportProblemOutlined style={{ fontSize: 16 }} />}
                            onClick={() => handleMoveToSpam(selectedEmail.id)}
                            disabled={loading}
                          >
                            {i18n.t("common.emailInbox.markAsSpam")}
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          className={classes.dangerButton}
                          startIcon={<Delete style={{ fontSize: 16 }} />}
                          onClick={() => handleDeleteEmail(selectedEmail.id)}
                          disabled={loading}
                        >
                          {i18n.t("common.emailInbox.moveToTrash")}
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>

                <Divider style={{ margin: "16px 0" }} />

                <div dangerouslySetInnerHTML={{ __html: selectedEmail.bodyHtml || selectedEmail.bodyText || "" }} />
                
                {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                  <div style={{ marginTop: 24, padding: 16, backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: 10, border: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12, fontSize: "0.9rem" }}>
                      {i18n.t("common.emailInbox.attachments")} ({selectedEmail.attachments.length}):
                    </Typography>
                    <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
                      {selectedEmail.attachments.map((att, idx) => {
                        const isImg = isImageAttachment(att);
                        const fileUrl = getAttachmentUrl(att);
                        const sizeStr = formatBytes(att.size);

                        if (att.error) {
                          return (
                            <Chip
                              key={idx}
                              icon={<ReportProblemOutlined style={{ fontSize: 16 }} />}
                              label={`${att.filename} (${att.error})`}
                              className={classes.attachmentChip}
                              size="small"
                            />
                          );
                        }

                        if (isImg) {
                          return (
                            <div key={idx} className={classes.attachmentCard}>
                              <div
                                style={{
                                  position: "relative",
                                  width: "100%",
                                  height: 110,
                                  borderRadius: 6,
                                  overflow: "hidden",
                                  cursor: "pointer",
                                  backgroundColor: "rgba(0,0,0,0.25)"
                                }}
                                onClick={() => setPreviewModalMedia({ url: fileUrl, filename: att.filename, size: att.size })}
                              >
                                <img
                                  src={fileUrl}
                                  alt={att.filename}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "rgba(0,0,0,0.45)",
                                    opacity: 0,
                                    transition: "opacity 0.2s ease"
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                                  onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
                                >
                                  <Visibility style={{ color: "#ffffff", fontSize: 28 }} />
                                </div>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6, padding: "0 2px" }}>
                                <div style={{ overflow: "hidden", minWidth: 0, flex: 1, marginRight: 4 }}>
                                  <Typography variant="caption" style={{ fontWeight: 600, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={att.filename}>
                                    {att.filename}
                                  </Typography>
                                  {sizeStr && (
                                    <Typography variant="caption" style={{ color: "#94a3b8", fontSize: "0.7rem", display: "block" }}>
                                      {sizeStr}
                                    </Typography>
                                  )}
                                </div>
                                <Tooltip title={i18n.t("common.emailInbox.download", "Descargar")}>
                                  <IconButton size="small" onClick={() => handleDownloadAttachment(att)} style={{ padding: 4 }}>
                                    <GetApp style={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div key={idx} className={classes.attachmentCardDoc}>
                            <Description style={{ fontSize: 32, color: theme.palette.primary.main, flexShrink: 0 }} />
                            <div style={{ overflow: "hidden", flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" style={{ fontWeight: 600, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={att.filename}>
                                {att.filename}
                              </Typography>
                              {sizeStr && (
                                <Typography variant="caption" style={{ color: "#94a3b8", fontSize: "0.72rem", display: "block" }}>
                                  {sizeStr}
                                </Typography>
                              )}
                            </div>
                            <Tooltip title={i18n.t("common.emailInbox.download", "Descargar")}>
                              <IconButton size="small" onClick={() => handleDownloadAttachment(att)} style={{ padding: 4 }}>
                                <GetApp style={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        );
                      })}
                    </Box>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={classes.welcomeMsg}>
              <div>
                <MailOutlineIcon style={{ fontSize: 72, opacity: 0.35, marginBottom: 16 }} />
                <Typography variant="h6" style={{ fontWeight: 600, opacity: 0.7 }}>
                  {i18n.t("common.emailInbox.selectEmailToRead", "Selecciona un correo para leer")}
                </Typography>
                <Typography variant="body2" style={{ opacity: 0.45, marginTop: 6 }}>
                  {i18n.t("common.emailInbox.selectEmailSub", "Selecciona un correo de la lista izquierda para ver sus detalles")}
                </Typography>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Settings Dialog (Añadir / Configurar Cuentas) */}
      <Dialog 
        open={openSettings} 
        onClose={() => setOpenSettings(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          className: classes.dialogPaper
        }}
      >
        <div className={classes.dialogHeader}>
          <h2>
            <span style={{ marginRight: 10, display: "flex", alignItems: "center" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.palette.primary.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
            </span> 
            {i18n.t("common.emailInbox.addAccount")}
          </h2>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Tooltip
              title={i18n.t("common.emailInbox.help.addAccountSummary")}
              arrow
              placement="bottom"
            >
              <IconButton size="small" style={{ color: theme.palette.primary.main, marginRight: 8, padding: 4 }}>
                <HelpOutline fontSize="small" />
              </IconButton>
            </Tooltip>
            <span style={{ fontSize: "0.85rem", color: theme.palette.type === 'dark' ? "#aaa" : "#666", marginRight: 16 }}>BotMix | CRM</span>
            <IconButton size="small" onClick={() => setOpenSettings(false)} style={{ color: theme.palette.type === 'dark' ? "#fff" : "#333", padding: 4 }}>
              <Close fontSize="small" />
            </IconButton>
          </div>
        </div>

        <div className={classes.dialogContent}>
          {accounts.map(acc => (
            <div key={acc.id} className={classes.accountBox}>
              <div>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>{acc.name}</Typography>
                <Typography variant="caption" color="textSecondary">{acc.email}</Typography>
              </div>
              <IconButton size="small" onClick={() => handleDeleteAccount(acc.id)} style={{ color: "#ef4444" }}>
                <Delete fontSize="small" />
              </IconButton>
            </div>
          ))}
          
          {accounts.length < maxConnections && (
            <div style={{ marginTop: accounts.length > 0 ? 20 : 0 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "24px" }}>
                <Typography variant="body2" style={{ textAlign: "center", color: theme.palette.text.secondary }}>
                  {i18n.t("common.emailInbox.fastSecureConnection")}
                </Typography>
                <Button 
                  variant="contained" 
                  style={{ backgroundColor: "#0078D4", color: "white", padding: "10px", borderRadius: "8px", fontWeight: "bold", textTransform: "none" }}
                  onClick={() => window.location.href = `${process.env.REACT_APP_BACKEND_URL}/oauth/microsoft?companyId=${user?.companyId || 1}`}
                >
                  {i18n.t("common.emailInbox.connectMicrosoft")}
                </Button>
                <Button 
                  variant="contained" 
                  style={{ backgroundColor: "#DB4437", color: "white", padding: "10px", borderRadius: "8px", fontWeight: "bold", textTransform: "none" }}
                  onClick={() => window.location.href = `${process.env.REACT_APP_BACKEND_URL}/oauth/google?companyId=${user?.companyId || 1}`}
                >
                  {i18n.t("common.emailInbox.connectGoogle")}
                </Button>
              </div>

              <Typography variant="body2" style={{ textAlign: "center", marginBottom: "16px", color: theme.palette.text.secondary }}>
                {i18n.t("common.emailInbox.manualConfig")}
              </Typography>

              <div className={classes.formInputContainer}>
                <label className={classes.formInputLabel}>{i18n.t("common.emailInbox.name")}</label>
                <input className={classes.formInput} placeholder="(p. ej., Juan Pérez)" value={accForm.name} onChange={e => setAccForm({...accForm, name: e.target.value})} />
              </div>

              <div className={classes.formInputContainer}>
                <label className={classes.formInputLabel}>{i18n.t("common.emailInbox.email")}</label>
                <input className={classes.formInput} placeholder="(p. ej., juan@empresa.com)" value={accForm.email} onChange={e => setAccForm({...accForm, email: e.target.value})} />
              </div>

              <div className={classes.formInputContainer}>
                <label className={classes.formInputLabel}>{i18n.t("common.emailInbox.password")}</label>
                <input type="password" className={classes.formInput} placeholder="(p. ej., •••••••••)" value={accForm.password} onChange={e => setAccForm({...accForm, password: e.target.value})} />
              </div>
              
              <div className={classes.advancedConfigBox}>
                <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 12 }}>
                  {i18n.t("common.emailInbox.advancedConfig")}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <div className={classes.formInputContainer} style={{ marginBottom: 12 }}>
                      <label className={classes.formInputLabel}>IMAP Host</label>
                      <input className={classes.formInput} placeholder="(p. ej., imap.servidor.com)" value={accForm.imapHost} onChange={e => setAccForm({...accForm, imapHost: e.target.value})} />
                    </div>
                  </Grid>
                  <Grid item xs={4}>
                    <div className={classes.formInputContainer} style={{ marginBottom: 12 }}>
                      <label className={classes.formInputLabel}>IMAP Port</label>
                      <input type="number" className={classes.formInput} placeholder="(p. ej., 993)" value={accForm.imapPort} onChange={e => setAccForm({...accForm, imapPort: Number(e.target.value)})} />
                    </div>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <div className={classes.formInputContainer} style={{ marginBottom: 0 }}>
                      <label className={classes.formInputLabel}>SMTP Host</label>
                      <input className={classes.formInput} placeholder="(p. ej., smtp.servidor.com)" value={accForm.smtpHost} onChange={e => setAccForm({...accForm, smtpHost: e.target.value})} />
                    </div>
                  </Grid>
                  <Grid item xs={4}>
                    <div className={classes.formInputContainer} style={{ marginBottom: 0 }}>
                      <label className={classes.formInputLabel}>SMTP Port</label>
                      <input type="number" className={classes.formInput} placeholder="(p. ej., 465)" value={accForm.smtpPort} onChange={e => setAccForm({...accForm, smtpPort: Number(e.target.value)})} />
                    </div>
                  </Grid>
                </Grid>
              </div>
            </div>
          )}
          {accounts.length >= maxConnections && (
            <Typography variant="body2" style={{ color: "#ef4444", marginTop: 12, textAlign: "center", fontWeight: 500 }}>
              {i18n.t("common.emailInbox.maxAccountsReached", { max: maxConnections })}
            </Typography>
          )}
        </div>
        
        <div className={classes.dialogActions}>
          <Button className={classes.dangerButton} onClick={() => setOpenSettings(false)}>
            {i18n.t("common.cancel") || "Cancelar"}
          </Button>
          {accounts.length < maxConnections && (
            <Button className={classes.applyButton} onClick={handleSaveAccount}>
              {i18n.t("common.emailInbox.saveChanges")}
            </Button>
          )}
        </div>
      </Dialog>

      {/* Compose Dialog (Redactar Correo) */}
      <Dialog 
        open={openCompose} 
        onClose={() => setOpenCompose(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          className: classes.dialogPaper
        }}
      >
        <div className={classes.dialogHeader}>
          <h2>
            <span style={{ marginRight: 10, display: "flex", alignItems: "center" }}>
              <Send style={{ fontSize: 20, color: theme.palette.primary.main }} />
            </span>
            {i18n.t("common.emailInbox.compose")}
          </h2>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Tooltip
              title={i18n.t("common.emailInbox.help.composeSummary")}
              arrow
              placement="bottom"
            >
              <IconButton size="small" style={{ color: theme.palette.primary.main, marginRight: 8, padding: 4 }}>
                <HelpOutline fontSize="small" />
              </IconButton>
            </Tooltip>
            <span style={{ fontSize: "0.85rem", color: theme.palette.type === 'dark' ? "#aaa" : "#666", marginRight: 16 }}>BotMix | CRM</span>
            <IconButton size="small" onClick={() => setOpenCompose(false)} style={{ color: theme.palette.type === 'dark' ? "#fff" : "#333", padding: 4 }}>
              <Close fontSize="small" />
            </IconButton>
          </div>
        </div>

        <div className={classes.dialogContent}>
          <div className={classes.formInputContainer}>
            <label className={classes.formInputLabel}>Para</label>
            <input
              className={classes.formInput}
              placeholder="destinatario@ejemplo.com"
              value={composeForm.to}
              onChange={e => setComposeForm({ ...composeForm, to: e.target.value })}
            />
          </div>

          <div className={classes.formInputContainer}>
            <label className={classes.formInputLabel}>Asunto</label>
            <input
              className={classes.formInput}
              placeholder="Asunto del correo"
              value={composeForm.subject}
              onChange={e => setComposeForm({ ...composeForm, subject: e.target.value })}
            />
          </div>

          <div className={classes.formInputContainer}>
            <label className={classes.formInputLabel}>Mensaje</label>
            <textarea
              className={classes.formInput}
              rows={8}
              style={{ resize: "vertical", fontFamily: "inherit" }}
              placeholder="Escribe tu mensaje aquí..."
              value={composeForm.bodyHtml}
              onChange={e => setComposeForm({ ...composeForm, bodyHtml: e.target.value })}
            />
          </div>

          {/* Adjuntos */}
          <div style={{ marginTop: 16, marginBottom: 8, display: "flex", alignItems: "center" }}>
            <input
              type="file"
              multiple
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <Button
              variant="outlined"
              size="small"
              className={classes.secondaryButton}
              startIcon={<AttachFile style={{ fontSize: 16 }} />}
              onClick={() => fileInputRef.current?.click()}
            >
              {i18n.t("common.emailInbox.attachFiles")}
            </Button>
            {attachments.length > 0 && (
              <span style={{ fontSize: "0.85rem", color: theme.palette.text.secondary, marginLeft: 12 }}>
                ({attachments.length} {i18n.t("common.emailInbox.attachments").toLowerCase()})
              </span>
            )}
          </div>

          {attachments.length > 0 && (
            <Box display="flex" flexWrap="wrap" style={{ marginTop: 8, gap: 8 }}>
              {attachments.map((file, idx) => (
                <ComposeAttachmentItem
                  key={idx}
                  file={file}
                  onRemove={() => handleRemoveAttachment(idx)}
                  onPreview={(media) => setPreviewModalMedia(media)}
                  classes={classes}
                />
              ))}
            </Box>
          )}
        </div>

        <div className={classes.dialogActions}>
          <Button
            className={classes.dangerButton}
            onClick={() => setOpenCompose(false)}
            disabled={loading}
          >
            {i18n.t("common.cancel")}
          </Button>
          <Button
            className={classes.applyButton}
            onClick={handleSendEmail}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Send style={{ fontSize: 16 }} />}
          >
            {loading ? "Enviando..." : "Enviar Correo"}
          </Button>
        </div>
      </Dialog>

      {/* Lightbox / Preview Dialog de Adjuntos */}
      <Dialog
        open={Boolean(previewModalMedia)}
        onClose={() => setPreviewModalMedia(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
            borderRadius: 16,
            overflow: "hidden"
          }
        }}
      >
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "14px 20px",
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, maxWidth: "80%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {previewModalMedia?.filename || previewModalMedia?.name || i18n.t("common.emailInbox.preview", "Vista previa")}
          </Typography>
          <IconButton size="small" onClick={() => setPreviewModalMedia(null)}>
            <Close style={{ fontSize: 20 }} />
          </IconButton>
        </div>
        <div style={{
          padding: 20,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: theme.palette.type === "dark" ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.03)",
          minHeight: 280,
          maxHeight: "75vh",
          overflow: "auto"
        }}>
          {previewModalMedia?.url && (
            <img
              src={previewModalMedia.url}
              alt={previewModalMedia.filename || previewModalMedia.name}
              style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}
            />
          )}
        </div>
        <div style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          padding: "12px 20px",
          borderTop: `1px solid ${theme.palette.divider}`
        }}>
          <Button
            size="small"
            variant="outlined"
            className={classes.secondaryButton}
            onClick={() => setPreviewModalMedia(null)}
          >
            {i18n.t("common.close", "Cerrar")}
          </Button>
          {!previewModalMedia?.file && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              className={classes.applyButton}
              startIcon={<GetApp style={{ fontSize: 18 }} />}
              onClick={() => handleDownloadAttachment(previewModalMedia)}
            >
              {i18n.t("common.emailInbox.download", "Descargar")}
            </Button>
          )}
        </div>
      </Dialog>
    </MainContainer>
  );
};

export default Emails;
